import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Activity, ArrowRight, Bell, ChevronRight, Download, Info, RefreshCw, TrendingUp, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import FaqSection from '../components/common/FaqSection';
// Discussion section hidden for now
// import GmpDiscussion from '../components/gmp/GmpDiscussion';
import DrhpStatCards from '../components/drhp/DrhpStatCards';
import DrhpLifecycleGuide from '../components/drhp/DrhpLifecycleGuide';
import DrhpTable from '../components/drhp/DrhpTable';
import DrhpSidebar from '../components/drhp/DrhpSidebar';
import { DRHP_FAQS } from '../data/drhpTrackerData';
import {
  DRHP_STATUS,
  DRHP_TABS,
  buildDrhpCsv,
  buildDrhpRows,
  computeDrhpStats,
  drhpTabByKey,
  filterDrhpRows,
  topMerchantBankers,
} from '../utils/drhpTracker';
import { formatRefreshedAt } from '../utils/gmpBoard';
import { todayIso } from '../utils/ipoCalendar';
import { fetchDrhpDetails } from '../store/slices/drhpSlice';
import { fetchGmp, selectGmpBoardRows } from '../store/slices/gmpSlice';
import {
  fetchSmeUpcomingIpos,
  fetchUpcomingIpos,
  selectOpeningSoonIpos,
  selectSmeOpeningSoonIpos,
} from '../store/slices/upcomingIposSlice';

const NOTICE_MS = 4500;
const DATA_MAX_AGE_MS = 5 * 60 * 1000;
const HEADING = 'IPO Prospectus PDF & Regulatory Filings';

// const DISCUSSION_COPY = {
//   headingId: 'drhp-discussion-heading',
//   title: 'DRHP Discussion & Filing Analysis',
//   subtitle: 'Discuss new draft filings, valuations and red flags spotted in offer documents with fellow investors.',
//   placeholder: 'Share what stood out in a DRHP: objects of the issue, promoter holding, litigation or peer valuation...',
//   submitLabel: 'Post Comment',
//   startLabel: 'Start New Discussion',
// };

export const DrhpTrackerPage = () => {
  const dispatch = useDispatch();
  const today = useMemo(() => todayIso(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = drhpTabByKey(searchParams.get('tab')).key;
  const [status, setStatus] = useState('all');
  const [exchange, setExchange] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [compact, setCompact] = useState(false);
  const [notice, setNotice] = useState(null);
  const tableRef = useRef(null);

  // GET /api/core/ipos (+ /{id} for extracted DRHPs) is the registry; the GMP
  // board and NSE schedules tell us which filings have reached the RHP stage
  const records = useSelector((state) => state.drhp.records);
  const detailsById = useSelector((state) => state.drhp.detailsById);
  const registryStatus = useSelector((state) => state.drhp.status);
  const registryError = useSelector((state) => state.drhp.error);
  const fetchedAt = useSelector((state) => state.drhp.fetchedAt);
  const gmpRows = useSelector(selectGmpBoardRows);
  const mainboardSchedule = useSelector(selectOpeningSoonIpos);
  const smeSchedule = useSelector(selectSmeOpeningSoonIpos);

  const loadFeeds = (force = false) => {
    const options = force ? {} : { maxAgeMs: DATA_MAX_AGE_MS };
    dispatch(fetchDrhpDetails(options));
    dispatch(fetchGmp(options));
    dispatch(fetchUpcomingIpos(options));
    dispatch(fetchSmeUpcomingIpos(options));
  };

  useEffect(() => {
    loadFeeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const scheduleRows = useMemo(() => [...mainboardSchedule, ...smeSchedule], [mainboardSchedule, smeSchedule]);
  const FILINGS = useMemo(
    () => buildDrhpRows({ records, detailsById, gmpRows, scheduleRows }),
    [records, detailsById, gmpRows, scheduleRows]
  );
  const stats = useMemo(() => computeDrhpStats(FILINGS), [FILINGS]);
  const merchantBankers = useMemo(() => topMerchantBankers(FILINGS), [FILINGS]);
  const tabCounts = useMemo(
    () => Object.fromEntries(DRHP_TABS.map((option) => [option.key, FILINGS.filter(option.test).length])),
    [FILINGS]
  );
  const rows = useMemo(() => filterDrhpRows(FILINGS, { tab, status, exchange, query }), [FILINGS, tab, status, exchange, query]);

  const isLoading = !FILINGS.length && (registryStatus === 'idle' || registryStatus === 'loading');
  const registryFailed = !FILINGS.length && registryStatus === 'failed';
  const refreshedLabel = formatRefreshedAt(fetchedAt);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `DRHP Filings Tracker: ${HEADING} | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [tab, status, exchange, query]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const changeTab = (key) => setSearchParams(key === 'all' ? {} : { tab: key }, { replace: true });

  const clearFilters = () => {
    setStatus('all');
    setExchange('all');
    setQuery('');
  };

  const changePage = (nextPage) => {
    setPage(nextPage);
    // Paging from the bottom of a long table: bring its first row back into view
    if (tableRef.current && tableRef.current.getBoundingClientRect().top < 0) {
      tableRef.current.scrollIntoView({ block: 'start' });
    }
  };

  const handleExport = () => {
    // BOM so spreadsheet apps read ₹ correctly
    const blob = new Blob(['﻿', buildDrhpCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `drhp-filings-${tab}-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const showAlertsNotice = () =>
    setNotice('DRHP filing alerts are coming soon. New filings appear in this tracker as soon as they reach our registry.');

  let emptyMessage;
  if (registryFailed) {
    emptyMessage = (
      <>
        The filings registry is temporarily unavailable{registryError ? ` (${registryError})` : ''}.{' '}
        <button type="button" onClick={() => loadFeeds(true)} className="font-bold text-[#008080] hover:underline">
          Retry
        </button>
      </>
    );
  } else if (!FILINGS.length) {
    emptyMessage = 'No offer documents are in the registry yet. New DRHP filings appear here as soon as they are added.';
  } else if (status !== 'all' || exchange !== 'all' || query) {
    emptyMessage = (
      <>
        No filings match these filters.{' '}
        <button type="button" onClick={clearFilters} className="font-bold text-[#008080] hover:underline">
          Clear filters
        </button>
      </>
    );
  } else {
    emptyMessage = `No filings in "${drhpTabByKey(tab).label}" yet.`;
  }

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/ipo-dashboard" className="hover:text-[#008080] transition-colors">
                Primary Market
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-900 font-semibold" aria-current="page">
                DRHP &amp; IPO Prospectus Tracker
              </span>
            </nav>

            <div className="mt-3 flex flex-col xl:flex-row xl:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] font-bold text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
                    Live Filing Registry
                  </span>
                  <span className="inline-flex px-2.5 py-1 rounded-full border border-slate-200 bg-white text-[11px] font-bold text-slate-600">
                    SEBI &amp; Exchange Offer Documents
                  </span>
                </div>
                <h1 className="mt-2.5 text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">{HEADING}</h1>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Track every <strong className="text-slate-700">Draft Red Herring Prospectus (DRHP)</strong>,{' '}
                  <strong className="text-slate-700">Red Herring Prospectus (RHP)</strong> and final prospectus across
                  Mainboard and SME issues, from SEBI review to RHP approval, bidding and listing, with extracted
                  financials and issue structure for analysed DRHPs.
                  {refreshedLabel && <span className="text-slate-400"> Updated {refreshedLabel}.</span>}
                </p>
              </div>

              {/* <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => loadFeeds(true)}
                  disabled={registryStatus === 'loading'}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${registryStatus === 'loading' ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={!rows.length}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
                <button
                  type="button"
                  onClick={showAlertsNotice}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#008080] hover:bg-[#006666] text-xs font-bold text-white shadow-sm transition-colors"
                >
                  <Bell className="w-3.5 h-3.5" />
                  Subscribe DRHP Alerts
                </button>
              </div> */}
            </div>
          </div>

          <DrhpStatCards stats={stats} loading={isLoading} />

          <DrhpLifecycleGuide />

          {/* Sidebar sits beside the table only from 1440px, where the table has room.
              Narrower screens stack it after the table. */}
          <div className="grid grid-cols-1 min-[1440px]:grid-cols-[minmax(0,1fr)_300px] gap-5 sm:gap-6 items-start">
            <div className="min-w-0">
              <DrhpTable
                ref={tableRef}
                rows={rows}
                tab={tab}
                onTabChange={changeTab}
                tabCounts={tabCounts}
                query={query}
                onQueryChange={setQuery}
                status={status}
                onStatusChange={setStatus}
                exchange={exchange}
                onExchangeChange={setExchange}
                page={page}
                onPageChange={changePage}
                compact={compact}
                onCompactChange={setCompact}
                isLoading={isLoading}
                emptyMessage={emptyMessage}
              />
              <p className="mt-2 text-[11px] text-slate-400 leading-relaxed">
                Review status and filing dates come from the DRHP tracker; once an approved issue has bidding dates, its
                stage follows the exchange schedule (“{DRHP_STATUS.bidding_soon.label}”, “{DRHP_STATUS.bidding_open.label}”).
                Offer documents open on the issue page.
              </p>
            </div>

            <DrhpSidebar merchantBankers={merchantBankers} analysedCount={stats.analysed} onAlertSignup={showAlertsNotice} />
          </div>

          <FaqSection
            id="drhp-faq"
            eyebrow="Investor Education"
            title="Understanding the Anatomy of an IPO Prospectus"
            subtitle="What each offer document contains, who prepares it and what to read before you bid."
            faqs={DRHP_FAQS}
          />

          {/* Discussion section hidden for now: <GmpDiscussion initialThreads={[]} copy={DISCUSSION_COPY} /> */}

          <aside
            aria-label="Live IPO market data"
            className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-950 via-[#05302f] to-[#006666] text-white p-6 sm:p-8 shadow-elevated"
          >
            <div aria-hidden="true" className="absolute -right-16 -top-20 w-72 h-72 rounded-full bg-teal-300/10 blur-2xl" />
            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <div className="max-w-2xl">
                <span className="inline-flex px-2 py-0.5 rounded bg-white/10 ring-1 ring-white/20 text-[10px] font-extrabold uppercase tracking-widest">
                  From Filing to Listing
                </span>
                <p className="mt-3 text-xl sm:text-2xl font-extrabold tracking-tight">Once the RHP is out, follow the live market</p>
                <p className="mt-2 text-sm text-teal-50/85 leading-relaxed">
                  Track grey market premium and category-wise live bidding for every issue that has moved past the DRHP stage.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 shrink-0">
                <Link
                  to="/ipo-gmp"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-white text-[#006666] text-sm font-extrabold hover:bg-teal-50 transition-colors"
                >
                  <TrendingUp className="w-4 h-4" />
                  Live IPO GMP
                </Link>
                <Link
                  to="/ipo-subscription"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-white/10 ring-1 ring-white/25 text-white text-sm font-extrabold hover:bg-white/20 transition-colors"
                >
                  <Activity className="w-4 h-4" />
                  Live Subscription
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default DrhpTrackerPage;
